/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import { useTranslation } from '@scuf/localization';
import React, { memo, useContext, useEffect, useState } from 'react';
import descriptors from '../../../../locales/en-US/descriptors';
import DetailSummaryComponent from '../../../shared/DetailSummaryComponent/DetailSummaryComponent';
import TrendChart from '../../../shared/TrendChart/TrendChart';
import { convertStackedDataForCarbonTrendChart } from '../../../shared/Utils/CarbonViewDetailsUtils';
import {
  IAny,
  ICarbonScopeOneStackedData
} from '../../../DashboardKPIWidgets/IEnergyManagementProps';
import {
  KPI_PERCENTAGE_DECIMAL_PRECISION,
  PERCENT_CHANGE_THRESHOLD
} from '../../../shared/Utils/Constants';
import { getCarbonTotalAggregationValues } from '../../../shared/Utils/unitHandling/EmaasSiteKpiDataScaling';
import {
  getConvertedValue,
  getMaxConvertedValueFromArray
} from '../../../shared/Utils/unitHandling/unitHandling';
import { ConversionType } from '../../../shared/Utils/unitHandling/UnitModel';
import StackedColumnChart from '../../../shared/StackedColumnChart/StackedColumnChart';
import { ApiContext } from '../../../../context/ApiContext';
import { useFilterScope1Context } from '../../../../context/FilterScope1Context/FilterScope1Context';

interface ICarbonScopeOneStackedDetailsComponentProps {
  data: ICarbonScopeOneStackedData;
  trendChartCategory: string;
  carbonAllSiteMonthData?: boolean;
  checkAllSitesData?: boolean;
  forSummary?: IAny;
  forGraph?: IAny;
  dataSourceType?: string;
  isSite?: boolean;
}
const CarbonScopeOneStackedDetailsComponentSeparateAPI = (
  props: ICarbonScopeOneStackedDetailsComponentProps
) => {
  const d = descriptors.energyManagement;
  const e = descriptors.DetailedView;
  const [t, lng] = useTranslation();
  const apiContext = useContext(ApiContext);
  const { data, carbonAllSiteMonthData, checkAllSitesData } = props;

  const valuesArrayForSummary = data
    ? [
        data?.totalAggregations?.totalActualEmission,
        data?.totalAggregations?.totalPreviousPeriodEmission,
        data?.totalAggregations?.totalDeviation
      ]
    : [];

  let maxValueUnitConversion: IAny;
  let aggregationSummaryData: IAny;

  if (props.forSummary && data) {
    maxValueUnitConversion = getMaxConvertedValueFromArray(
      ConversionType.CARBON,
      data?.emissionMeasurementUnit,
      valuesArrayForSummary
    );
    const getScaledValue = (value: number) => {
      return getConvertedValue(
        ConversionType.CARBON,
        data?.emissionMeasurementUnit,
        value,
        maxValueUnitConversion.Unit
      ).Value;
    };
    aggregationSummaryData = {
      actualCost: getConvertedValue(
        ConversionType.CARBON,
        data?.emissionMeasurementUnit,
        data?.totalAggregations?.totalActualEmission,
        getMaxConvertedValueFromArray(
          ConversionType.CARBON,
          data?.emissionMeasurementUnit,
          [data?.totalAggregations?.totalActualEmission]
        ).Unit
      ).Value,
      actualUnit: getMaxConvertedValueFromArray(
        ConversionType.CARBON,
        data?.emissionMeasurementUnit,
        [data?.totalAggregations?.totalActualEmission]
      ).Unit,
      baselineCost: getConvertedValue(
        ConversionType.CARBON,
        data?.emissionMeasurementUnit,
        data?.totalAggregations?.totalPreviousPeriodEmission,
        getMaxConvertedValueFromArray(
          ConversionType.CARBON,
          data?.emissionMeasurementUnit,
          [data?.totalAggregations?.totalPreviousPeriodEmission]
        ).Unit
      ).Value,
      baselineUnit: getMaxConvertedValueFromArray(
        ConversionType.CARBON,
        data?.emissionMeasurementUnit,
        [data?.totalAggregations?.totalPreviousPeriodEmission]
      ).Unit,
      costSavings:
        (apiContext.showChangeWhenData &&
          (isNaN(
            Number(data?.totalAggregations?.totalPreviousPeriodEmission)
          ) ||
            isNaN(Number(data?.totalAggregations?.totalActualEmission)) ||
            Number(data?.totalAggregations?.totalPreviousPeriodEmission) ===
              0 ||
            Number(data?.totalAggregations?.totalActualEmission) === 0 ||
            Math.abs(
              Number(data?.totalAggregations?.totalDeviationPercentage)
            ) > PERCENT_CHANGE_THRESHOLD)) ||
        !carbonAllSiteMonthData ||
        !checkAllSitesData
          ? null
          : getConvertedValue(
              ConversionType.CARBON,
              data?.emissionMeasurementUnit,
              data?.totalAggregations?.totalDeviation,
              getMaxConvertedValueFromArray(
                ConversionType.CARBON,
                data?.emissionMeasurementUnit,
                [data?.totalAggregations?.totalDeviation]
              ).Unit
            ).Value,
      changeUnit: getMaxConvertedValueFromArray(
        ConversionType.CARBON,
        data?.emissionMeasurementUnit,
        [data?.totalAggregations?.totalDeviation]
      ).Unit,
      costSavingsPercentage: parseFloat(
        data?.totalAggregations?.totalDeviationPercentage.toFixed(
          KPI_PERCENTAGE_DECIMAL_PRECISION
        )
      )
    };
  }

  const [chartData, setChartData] = useState(
    props.forGraph &&
      data &&
      convertStackedDataForCarbonTrendChart(
        data?.monthWiseEmissionByFuelType,
        data?.emissionMeasurementUnit,
        data?.totalSiteCount,
        lng
      )
  );
  const [state] = useFilterScope1Context();

  useEffect(() => {
    if (data && props.forGraph && state?.saveFilterData?.length > 0) {
      const selectedStacks = state.saveFilterData.map(
        (fuel: { boolVal: boolean; childName: string }) => {
          if (fuel.boolVal === true) return fuel.childName;
        }
      );
      let allChartData = convertStackedDataForCarbonTrendChart(
        data?.monthWiseEmissionByFuelType,
        data?.emissionMeasurementUnit,
        undefined,
        lng
      );
      const filteredStacks = allChartData.fuel.filter((stack) => {
        if (selectedStacks.includes(stack.name))
          return { ...stack, selected: true };
      });
      setChartData({
        ...chartData,
        fuel: filteredStacks
      });
    }
  }, [state]);

  return (
    <div className="scopeOneStackedColumnChart">
      {props.forSummary && (
        <DetailSummaryComponent
          aggregationData={aggregationSummaryData}
          measurementUnit={maxValueUnitConversion.Unit}
          detailType={t(e.SCOPEONEKEY)}
          renderedFromCarbon={true}
          fromCarbonScopeOne={true}
        />
      )}
      {props.forGraph && (
        <StackedColumnChart
          isSite={props.isSite}
          unit={chartData.unit}
          chartData={chartData}
          isAllDataAvailable={false}
          renderedFromCarbon={true}
          dataSourceType={props.dataSourceType}
        />
      )}
    </div>
  );
};

export default memo(CarbonScopeOneStackedDetailsComponentSeparateAPI);
